import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Svg,
  Path,
  G,
  Defs,
  ClipPath,
  Image,
} from "@react-pdf/renderer";
import {
  TreatmentPlanResponse,
  TreatmentPlanAppointment,
  PublicTreatmentPlanResponse,
} from "../../services/treatmentPlanService";

// Register fonts - using local woff font files from public/fonts
Font.register({
  family: "Nunito",
  fonts: [
    {
      src: "/fonts/Nunito-Regular.woff",
      fontWeight: 400,
    },
    {
      src: "/fonts/Nunito-Italic.woff",
      fontWeight: 400,
      fontStyle: "italic",
    },
    {
      src: "/fonts/Nunito-SemiBold.woff",
      fontWeight: 600,
    },
    {
      src: "/fonts/Nunito-Bold.woff",
      fontWeight: 700,
    },
  ],
});

Font.register({
  family: "Sora",
  fonts: [
    {
      src: "/fonts/Sora-Regular.woff",
      fontWeight: 400,
    },
    {
      src: "/fonts/Sora-SemiBold.woff",
      fontWeight: 600,
    },
    {
      src: "/fonts/Sora-Bold.woff",
      fontWeight: 700,
    },
  ],
});

// Colors matching the web components exactly
const colors = {
  primary: "#3A8AB9", // text.action - for appointment titles
  dark: "#0E426D", // text.link - for procedure titles, icons
  text: "#1B252F",
  secondary: "#6B7A94",
  background: "#F5F9FD",
  mediumBlue: "#E8F6F9",
  border: "#B9CED5", // border.medium
  borderLight: "#E0E0E0",
  white: "#FFFFFF",
};

// Styles - font sizes reduced by 50% for more compact PDF
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: "Nunito",
    fontSize: 6,
    color: colors.text,
    backgroundColor: colors.white,
  },
  header: {
    textAlign: "center",
    marginBottom: 12,
  },
  logo: {
    alignSelf: "center",
    marginBottom: 8,
  },
  title: {
    fontFamily: "Sora",
    fontSize: 10,
    fontWeight: 600,
    color: colors.text,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 7,
    color: colors.secondary,
  },
  personsInfo: {
    flexDirection: "row",
    gap: 30,
    padding: 8,
    paddingHorizontal: 12,
    backgroundColor: colors.background,
    borderRadius: 6,
    marginBottom: 12,
  },
  personLabel: {
    fontSize: 6,
    color: colors.secondary,
    marginBottom: 2,
  },
  personName: {
    fontFamily: "Sora",
    fontSize: 7,
    fontWeight: 600,
    color: colors.text,
  },
  section: {
    marginBottom: 12,
  },
  sectionTitle: {
    fontFamily: "Sora",
    fontSize: 8,
    fontWeight: 600,
    color: colors.text,
    marginBottom: 4,
  },
  sectionText: {
    fontSize: 7,
    color: colors.text,
    lineHeight: 1.5,
  },
  appointmentHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  appointmentTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  appointmentTitle: {
    fontFamily: "Sora",
    fontSize: 9,
    fontWeight: 600,
    color: colors.primary, // #3A8AB9 - matches text.action
  },
  optionBadge: {
    backgroundColor: colors.dark,
    color: colors.white,
    paddingVertical: 1,
    paddingHorizontal: 4,
    borderRadius: 2,
    fontSize: 5,
    fontWeight: 600,
  },
  appointmentMeta: {
    flexDirection: "row",
    gap: 12,
  },
  metaLabel: {
    fontSize: 8,
    fontWeight: 700,
    fontFamily: "Nunito",
    color: colors.text,
  },
  metaValue: {
    fontSize: 8,
    fontWeight: 700,
    fontFamily: "Nunito",
    color: colors.text,
  },
  appointmentDescription: {
    fontSize: 6,
    color: colors.secondary,
    marginBottom: 6,
  },
  // Procedure card - wrap={false} ensures it won't be split across pages
  procedureCard: {
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    padding: 12,
    marginBottom: 6,
    backgroundColor: colors.white,
  },
  procedureLayout: {
    flexDirection: "row",
    gap: 12,
    alignItems: "flex-end",
  },
  procedureContent: {
    flex: 1,
    minWidth: 0,
  },
  procedureTitle: {
    fontFamily: "Sora",
    fontSize: 8,
    fontWeight: 600,
    color: colors.dark, // #0E426D - matches text.link
    marginBottom: 4,
  },
  procedureDescription: {
    fontSize: 8,
    fontFamily: "Nunito",
    color: colors.text,
    fontStyle: "italic",
    marginBottom: 4,
  },
  procedureExplanation: {
    fontSize: 8,
    fontFamily: "Nunito",
    color: colors.text,
  },
  procedureMeta: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    width: 160,
    flexShrink: 0,
  },
  procedureMetaItem: {
    flexDirection: "column",
    gap: 2,
  },
  procedureMetaItemCdt: {
    flexDirection: "column",
    gap: 2,
    width: 60,
  },
  procedureMetaItemDuration: {
    flexDirection: "column",
    gap: 2,
    minWidth: 25,
  },
  procedureMetaItemFee: {
    flexDirection: "column",
    gap: 2,
  },
  procedureMetaLabel: {
    fontSize: 7,
    fontFamily: "Nunito",
    color: colors.secondary,
  },
  procedureMetaValue: {
    fontFamily: "Sora",
    fontSize: 7,
    fontWeight: 600,
    color: colors.text,
  },
  procedureFeeHint: {
    fontFamily: "Nunito",
    fontWeight: 400,
    fontSize: 7,
    color: colors.text,
  },
  // Time separator
  timeSeparator: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    height: 20,
    marginVertical: 4,
    backgroundColor: colors.mediumBlue,
    borderRadius: 4,
  },
  timeSeparatorText: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  timeSeparatorLabel: {
    fontFamily: "Sora",
    fontSize: 7,
    fontWeight: 600,
    color: colors.dark,
  },
  // Disclaimer
  disclaimer: {
    marginBottom: 8,
    fontSize: 5,
    color: colors.secondary,
    lineHeight: 1.4,
  },
  // Total fee
  totalFee: {
    textAlign: "right",
    marginBottom: 12,
  },
  totalFeeLabel: {
    fontFamily: "Sora",
    fontSize: 8,
    fontWeight: 600,
    color: colors.text,
  },
  totalFeeValue: {
    fontFamily: "Sora",
    fontSize: 9,
    fontWeight: 700,
    color: colors.text,
  },
  // Footer
  footer: {
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingTop: 8,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  footerLeft: {},
  footerGenerated: {
    fontSize: 6,
    color: colors.secondary,
    marginBottom: 4,
  },
  footerClinicName: {
    fontFamily: "Sora",
    fontSize: 8,
    fontWeight: 600,
    color: colors.text,
    marginBottom: 2,
  },
  footerClinicInfo: {
    fontSize: 7,
    color: colors.secondary,
  },
  // Signature
  signatureSection: {
    alignItems: "flex-end",
    minWidth: 100,
  },
  signatureLabel: {
    fontSize: 6,
    color: colors.secondary,
    marginBottom: 2,
    textAlign: "right",
  },
  signatureText: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.text,
    fontStyle: "italic",
    textAlign: "right",
  },
  signatureImage: {
    maxWidth: 100,
    maxHeight: 30,
    objectFit: "contain",
  },
  signatureDate: {
    fontSize: 5,
    textAlign: "right",
    color: colors.secondary,
    marginTop: 2,
  },
});

// Helper functions
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

const formatTime = (dateString: string): string => {
  return new Date(dateString).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const calculateAppointmentFee = (
  appointment: TreatmentPlanAppointment,
): number => {
  if (appointment.totalFee != null) return appointment.totalFee;
  let total = 0;
  appointment.steps.forEach((step) => {
    if (step.fee != null) {
      total += step.fee;
    } else {
      step.codeSuggestions.forEach((code) => {
        total += code.defaultFee || 0;
      });
    }
  });
  return total;
};

const getAppointmentDuration = (
  appointment: TreatmentPlanAppointment,
): number => {
  if (appointment.totalDurationMinutes != null)
    return appointment.totalDurationMinutes;
  let total = 0;
  appointment.steps.forEach((step) => {
    total += step.durationMinutes ?? 20;
  });
  return total;
};

const getProcedureFee = (
  step: TreatmentPlanAppointment["steps"][0],
): number => {
  if (step.fee != null) return step.fee;
  return step.codeSuggestions.reduce(
    (sum, code) => sum + (code.defaultFee || 0),
    0,
  );
};

// Format CDT codes - 2 per row
const formatCdtCodes = (
  codeSuggestions: TreatmentPlanAppointment["steps"][0]["codeSuggestions"],
): string => {
  const codes = codeSuggestions.map((code) => code.code);
  if (codes.length <= 2) {
    return codes.join(", ");
  }
  // Group codes in pairs
  const pairs: string[] = [];
  for (let i = 0; i < codes.length; i += 2) {
    if (i + 1 < codes.length) {
      pairs.push(`${codes[i]}, ${codes[i + 1]}`);
    } else {
      pairs.push(codes[i]);
    }
  }
  return pairs.join("\n");
};

const getCdtDescriptions = (
  codeSuggestions: TreatmentPlanAppointment["steps"][0]["codeSuggestions"],
): string => {
  const descriptions = codeSuggestions
    .map((code) => code.description)
    .filter(Boolean);
  return descriptions.join(" and ");
};

// Logo component
const Logo: React.FC = () => (
  <Svg width={75} height={17} viewBox="0 0 283 62" style={styles.logo}>
    <Path
      d="M274.338 45.2866C272.769 45.2866 271.409 45.0643 270.258 44.6197C269.134 44.1752 268.258 43.4168 267.63 42.3446C267.029 41.2463 266.728 39.7688 266.728 37.9121V17.75H270.337V38.3828C270.337 39.5073 270.637 40.3833 271.239 41.0109C271.867 41.6124 272.743 41.9131 273.867 41.9131H277.554V45.2866H274.338ZM263.119 26.9289V24.1046H277.554V26.9289H263.119Z"
      fill="#1B252F"
    />
    <Path
      d="M241.087 45.051V24.1044H244.069V33.0871H243.598C243.598 31.0213 243.872 29.2822 244.422 27.8701C244.997 26.4318 245.86 25.3466 247.011 24.6144C248.161 23.856 249.612 23.4768 251.365 23.4768H251.521C254.163 23.4768 256.111 24.3005 257.366 25.948C258.648 27.5955 259.288 29.9752 259.288 33.0871V45.051H255.523V32.3026C255.523 30.6813 255.065 29.3868 254.15 28.4193C253.234 27.4255 251.979 26.9287 250.384 26.9287C248.736 26.9287 247.403 27.4386 246.383 28.4585C245.363 29.4784 244.853 30.8382 244.853 32.538V45.051H241.087Z"
      fill="#1B252F"
    />
    <Path
      d="M225.974 45.7572C224.143 45.7572 222.561 45.4434 221.227 44.8158C219.894 44.1882 218.809 43.3644 217.972 42.3445C217.135 41.2985 216.507 40.1348 216.089 38.8534C215.697 37.5721 215.5 36.2645 215.5 34.9308V34.2248C215.5 32.865 215.697 31.5443 216.089 30.263C216.507 28.9816 217.135 27.831 217.972 26.8111C218.809 25.7651 219.868 24.9413 221.149 24.3399C222.457 23.7122 223.986 23.3984 225.738 23.3984C228.014 23.3984 229.883 23.8953 231.348 24.889C232.838 25.8566 233.937 27.1118 234.643 28.6547C235.375 30.1976 235.741 31.8451 235.741 33.5972V35.2839H217.148V32.4596H232.995L232.132 33.9894C232.132 32.4988 231.897 31.2175 231.426 30.1453C230.955 29.047 230.249 28.1971 229.308 27.5956C228.367 26.9941 227.177 26.6934 225.738 26.6934C224.248 26.6934 223.006 27.0334 222.012 27.7133C221.044 28.3932 220.312 29.3215 219.815 30.4983C219.345 31.6751 219.109 33.0349 219.109 34.5778C219.109 36.0684 219.345 37.4152 219.815 38.6181C220.312 39.7949 221.071 40.7363 222.09 41.4423C223.11 42.1223 224.405 42.4622 225.974 42.4622C227.621 42.4622 228.955 42.0961 229.975 41.3639C231.021 40.6317 231.648 39.7818 231.858 38.8142H235.388C235.1 40.2525 234.525 41.4946 233.662 42.5407C232.799 43.5867 231.714 44.3843 230.406 44.9334C229.099 45.4826 227.621 45.7572 225.974 45.7572Z"
      fill="#1B252F"
    />
    <Path
      d="M189.65 45.208V41.6777H196.789C198.515 41.6777 200.045 41.4031 201.378 40.8539C202.738 40.2786 203.889 39.4941 204.83 38.5004C205.798 37.5066 206.53 36.3429 207.027 35.0093C207.524 33.6756 207.772 32.2373 207.772 30.6944C207.772 29.0731 207.524 27.6086 207.027 26.3011C206.53 24.9674 205.798 23.8168 204.83 22.8492C203.889 21.8555 202.738 21.0971 201.378 20.5741C200.045 20.0511 198.515 19.7896 196.789 19.7896H189.65V16.2593H196.397C198.986 16.2593 201.235 16.6646 203.144 17.4753C205.053 18.2598 206.635 19.332 207.89 20.6918C209.171 22.0255 210.126 23.5291 210.753 25.2028C211.381 26.8503 211.695 28.537 211.695 30.2629V31.1259C211.695 32.7995 211.381 34.4732 210.753 36.1468C210.126 37.8204 209.171 39.3372 207.89 40.697C206.635 42.0568 205.053 43.1552 203.144 43.992C201.235 44.8026 198.986 45.208 196.397 45.208H189.65ZM186.59 45.208V16.2593H190.434V45.208H186.59Z"
      fill="#1B252F"
    />
    <Path
      d="M169.07 45.7962C167.187 45.7962 165.513 45.5085 164.049 44.9332C162.584 44.3318 161.342 43.5211 160.322 42.5012C159.329 41.4813 158.57 40.3176 158.047 39.0101C157.524 37.6764 157.263 36.2774 157.263 34.8129V33.9107C157.263 32.4202 157.524 31.008 158.047 29.6743C158.596 28.3145 159.381 27.1247 160.401 26.1048C161.447 25.0849 162.702 24.2873 164.166 23.712C165.631 23.1105 167.265 22.8098 169.07 22.8098C170.926 22.8098 172.574 23.1105 174.012 23.712C175.477 24.2873 176.719 25.0849 177.739 26.1048C178.758 27.1247 179.543 28.3145 180.092 29.6743C180.641 31.008 180.916 32.4202 180.916 33.9107V34.8129C180.916 36.2774 180.654 37.6764 180.131 39.0101C179.608 40.3176 178.837 41.4813 177.817 42.5012C176.823 43.5211 175.594 44.3318 174.13 44.9332C172.665 45.5085 170.979 45.7962 169.07 45.7962ZM169.07 40.5007C170.299 40.5007 171.319 40.2392 172.129 39.7162C172.94 39.1932 173.555 38.4609 173.973 37.5195C174.417 36.5781 174.64 35.5321 174.64 34.3815C174.64 33.1524 174.417 32.0802 173.973 31.1649C173.528 30.2235 172.888 29.4782 172.051 28.9291C171.24 28.3799 170.246 28.1053 169.07 28.1053C167.919 28.1053 166.925 28.3799 166.089 28.9291C165.252 29.4782 164.611 30.2235 164.166 31.1649C163.748 32.0802 163.539 33.1524 163.539 34.3815C163.539 35.5321 163.748 36.5781 164.166 37.5195C164.585 38.4609 165.212 39.1932 166.049 39.7162C166.886 40.2392 167.893 40.5007 169.07 40.5007Z"
      fill="#1B252F"
    />
    <Path
      d="M139.847 45.0512L133.414 23.5554H139.886L145.927 45.0512H139.847ZM141.808 45.0512V39.8342H148.124V45.0512H141.808ZM144.123 45.0512L149.34 23.5554H155.381L149.81 45.0512H144.123Z"
      fill="#1B252F"
    />
    <Path
      d="M123.608 45.0512V23.5554H129.884V45.0512H123.608ZM120.745 28.1841V23.5554H129.884V28.1841H120.745ZM126.04 21.4372C124.863 21.4372 123.987 21.1365 123.412 20.535C122.863 19.9074 122.588 19.1229 122.588 18.1814C122.588 17.2139 122.863 16.4294 123.412 15.8279C123.987 15.2003 124.863 14.8865 126.04 14.8865C127.217 14.8865 128.08 15.2003 128.629 15.8279C129.204 16.4294 129.492 17.2139 129.492 18.1814C129.492 19.1229 129.204 19.9074 128.629 20.535C128.08 21.1365 127.217 21.4372 126.04 21.4372Z"
      fill="#1B252F"
    />
    <Path
      d="M106.078 45.0511V23.5553H111.06V32.7734H110.942C110.942 29.74 111.583 27.3995 112.864 25.752C114.172 24.1045 116.055 23.2808 118.513 23.2808H119.336V28.6939H117.767C116.041 28.6939 114.708 29.1646 113.766 30.1061C112.825 31.0213 112.354 32.355 112.354 34.1071V45.0511H106.078Z"
      fill="#1B252F"
    />
    <Path
      d="M86.2589 45.7964C83.67 45.7964 81.4079 45.3518 79.4728 44.4627C77.5638 43.5474 75.9817 42.3576 74.7265 40.8931C73.4712 39.4026 72.5298 37.7943 71.9022 36.0684C71.3007 34.3424 71 32.6819 71 31.0867V30.2237C71 28.4455 71.3138 26.6934 71.9414 24.9674C72.5952 23.2153 73.5628 21.6332 74.8441 20.2211C76.1255 18.809 77.7207 17.6845 79.6297 16.8477C81.5387 15.9847 83.7484 15.5532 86.2589 15.5532C88.7693 15.5532 90.979 15.9847 92.888 16.8477C94.797 17.6845 96.3922 18.809 97.6736 20.2211C98.955 21.6332 99.9226 23.2153 100.576 24.9674C101.23 26.6934 101.557 28.4455 101.557 30.2237V31.0867C101.557 32.6819 101.243 34.3424 100.616 36.0684C99.9879 37.7943 99.0465 39.4026 97.7913 40.8931C96.536 42.3576 94.9409 43.5474 93.0057 44.4627C91.0967 45.3518 88.8478 45.7964 86.2589 45.7964ZM86.2589 39.7948C87.6187 39.7948 88.8347 39.5595 89.9069 39.0888C91.0052 38.618 91.9335 37.9643 92.6919 37.1275C93.4764 36.2906 94.0648 35.3231 94.4571 34.2247C94.8493 33.1264 95.0455 31.9496 95.0455 30.6944C95.0455 29.3607 94.8363 28.1447 94.4179 27.0464C94.0256 25.9219 93.4372 24.9544 92.6527 24.1437C91.8943 23.3069 90.9791 22.6662 89.9069 22.2216C88.8347 21.7771 87.6187 21.5548 86.2589 21.5548C84.899 21.5548 83.683 21.7771 82.6109 22.2216C81.5387 22.6662 80.6103 23.3069 79.8258 24.1437C79.0675 24.9544 78.4921 25.9219 78.0999 27.0464C77.7076 28.1447 77.5115 29.3607 77.5115 30.6944C77.5115 31.9496 77.7076 33.1264 78.0999 34.2247C78.4921 35.3231 79.0675 36.2906 79.8258 37.1275C80.6103 37.9643 81.5387 38.618 82.6109 39.0888C83.683 39.5595 84.899 39.7948 86.2589 39.7948Z"
      fill="#1B252F"
    />
    <Path
      d="M17.7722 58.1546C17.7722 58.1546 16.4273 60.9478 12.8066 60.9478C9.18596 60.9478 8.15143 57.7409 8.15143 57.7409L0.841173 37.238C0.284525 35.6768 3.01116e-06 34.0316 2.38724e-06 32.3742L0 7.85645C0 2.60645 3.50002 0.947754 7.00001 0.947754H35.5C39.75 0.947754 42 4.10645 42 7.60644V12.3564H47.75C48.75 12.3564 50.1338 12.5293 51.75 14.1064C53.4063 15.7227 53.5653 18.3564 53.5653 18.3564V34.0201C53.5653 35.7509 53.2568 37.463 52.6513 39.0845C51.0293 43.4288 47.6365 52.5209 45.7032 57.7409C45.7032 57.7409 44.1514 60.9478 40.9446 60.9478C37.6278 60.9478 36.4963 58.5684 36.4963 58.5684L31.0135 43.3615C31.0135 43.3615 29.6687 40.7753 27.1125 40.7753C23.7722 40.7753 22.7377 43.3615 22.7377 43.3615L17.7722 58.1546Z"
      fill="#5BB2DF"
    />
    <Defs>
      <ClipPath id="mask0">
        <Path
          d="M17.7722 58.1546C17.7722 58.1546 16.4273 60.9478 12.8066 60.9478C9.18596 60.9478 8.15143 57.7409 8.15143 57.7409L0.841173 37.238C0.284525 35.6768 3.03962e-06 34.0431 2.82208e-06 32.3856L0 15.4308C0 7.43216 6.48415 0.947754 14.4828 0.947754H36.2958C39.4462 0.947754 42 3.5016 42 6.65193C42 9.79265 44.5389 12.3425 47.6796 12.3561L47.7702 12.3564C50.9707 12.3564 53.5653 14.9498 53.5653 18.1503V34.0095C53.5653 35.7518 53.2527 37.4677 52.6403 39.0989C51.0082 43.4465 47.6096 52.5182 45.7032 57.7409C45.7032 57.7409 44.1514 60.9478 40.9446 60.9478C37.6278 60.9478 36.4963 58.5684 36.4963 58.5684L31.0135 43.3615C31.0135 43.3615 29.6687 40.7753 27.1125 40.7753C23.7722 40.7753 22.7377 43.3615 22.7377 43.3615L17.7722 58.1546Z"
          fill="#5BB2DF"
        />
      </ClipPath>
    </Defs>
    <G clipPath="url(#mask0)">
      <Path
        d="M15.5 12.3273H47C47 12.3273 50.3461 12.1367 52 14.1067C53.3884 15.7605 53.5864 17.8567 53.5864 17.8567V60.948H9.31055V18.8567C9.31055 17.1067 9.49998 12.3273 15.5 12.3273Z"
        fill="#3386B8"
      />
      <Path
        d="M42.0002 12.3271H47C47 12.3271 50.25 12.1949 52 14.1065C53.59 15.8433 53.5865 18.3566 53.5865 18.3566V60.9478H42.0002V12.3271Z"
        fill="#4E92C1"
      />
      <Path
        d="M19.75 25.8565C21.3074 24.0611 24.5 24.3271 24.5 24.3271H53.5865V60.9478H18.4141V29.6065C18.4141 29.6065 18.3991 27.4139 19.75 25.8565Z"
        fill="#347AAD"
      />
      <Path
        d="M20 25.8566C21.6727 24.1682 24.5 24.3272 24.5 24.3272H42.2071C42.2071 24.3272 42.4906 34.5746 41 39.6066C38.9219 46.6222 30.2071 52.1548 30.2071 52.1548L18.414 60.9479V30.3566C18.414 30.3566 18.3428 27.5294 20 25.8566Z"
        fill="#1B6B9E"
      />
    </G>
  </Svg>
);

// Calendar icon component
const CalendarIcon: React.FC = () => (
  <Svg width={10} height={10} viewBox="0 0 25 24">
    <Path
      d="M7.7998 11H9.7998V13H7.7998V11ZM7.7998 15H9.7998V17H7.7998V15ZM11.7998 11H13.7998V13H11.7998V11ZM11.7998 15H13.7998V17H11.7998V15ZM15.7998 11H17.7998V13H15.7998V11ZM15.7998 15H17.7998V17H15.7998V15Z"
      fill="#0E426D"
    />
    <Path
      d="M5.7998 22H19.7998C20.9028 22 21.7998 21.103 21.7998 20V6C21.7998 4.897 20.9028 4 19.7998 4H17.7998V2H15.7998V4H9.7998V2H7.7998V4H5.7998C4.6968 4 3.7998 4.897 3.7998 6V20C3.7998 21.103 4.6968 22 5.7998 22ZM19.7998 8L19.8008 20H5.7998V8H19.7998Z"
      fill="#0E426D"
    />
  </Svg>
);

// Right Arrow icon component (rotated 90 degrees = down arrow)
const DownArrowIcon: React.FC = () => (
  <Svg
    width={10}
    height={10}
    viewBox="0 0 20 21"
    style={{ transform: "rotate(90deg)" }}
  >
    <Path
      d="M9.41083 14.9109L10.5892 16.0892L16.1783 10.5001L10.5892 4.91089L9.41083 6.08922L12.9883 9.66672H5V11.3334H12.9883L9.41083 14.9109Z"
      fill="#0E426D"
    />
  </Svg>
);

// Procedure Card component - wrap={false} ensures it won't be split
interface ProcedureCardProps {
  step: TreatmentPlanAppointment["steps"][0];
}

const ProcedureCard: React.FC<ProcedureCardProps> = ({ step }) => {
  const fee = getProcedureFee(step);
  const cdtCodes = formatCdtCodes(step.codeSuggestions);
  const cdtDescription = getCdtDescriptions(step.codeSuggestions);

  return (
    <View style={styles.procedureCard} wrap={false}>
      <View style={styles.procedureLayout}>
        <View style={styles.procedureContent}>
          <Text style={styles.procedureTitle}>{step.name}</Text>
          {step.description && (
            <Text style={styles.procedureDescription}>{step.description}</Text>
          )}
          {cdtDescription && (
            <Text style={styles.procedureExplanation}>{cdtDescription}</Text>
          )}
        </View>
        <View style={styles.procedureMeta}>
          <View style={styles.procedureMetaItemCdt}>
            <Text style={styles.procedureMetaLabel}>CDT Code:</Text>
            <Text style={styles.procedureMetaValue}>{cdtCodes || "-"}</Text>
          </View>
          <View style={styles.procedureMetaItemFee}>
            <Text style={styles.procedureMetaLabel}>Fee:</Text>
            <Text style={styles.procedureMetaValue}>
              ${fee.toLocaleString("en-US")}{" "}
              <Text style={styles.procedureFeeHint}>
                (based on clinic rates)
              </Text>
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
};

// Time Separator component
interface TimeSeparatorProps {
  timeframeLabel: string;
}

const TimeSeparator: React.FC<TimeSeparatorProps> = ({ timeframeLabel }) => (
  <View style={styles.timeSeparator} wrap={false}>
    <View style={styles.timeSeparatorText}>
      <CalendarIcon />
      <Text style={styles.timeSeparatorLabel}>{timeframeLabel}</Text>
      <DownArrowIcon />
    </View>
  </View>
);

// Appointment component
interface AppointmentProps {
  appointment: TreatmentPlanAppointment;
  appointmentNumber: number;
  isLast: boolean;
}

const Appointment: React.FC<AppointmentProps> = ({
  appointment,
  appointmentNumber,
  isLast,
}) => {
  const fee = calculateAppointmentFee(appointment);
  const duration = getAppointmentDuration(appointment);

  const getOptionLabel = () => {
    if (
      !appointment.conditionalPath ||
      appointment.conditionalPath.trim() === ""
    )
      return null;
    const rawPath = appointment.conditionalPath.trim();
    return rawPath.toLowerCase().startsWith("option")
      ? rawPath.replace(/^option\s*/i, "")
      : rawPath;
  };

  const optionLabel = getOptionLabel();

  return (
    <View>
      {/* Appointment header - wrap={false} keeps it together */}
      <View style={styles.appointmentHeader} wrap={false}>
        <View style={styles.appointmentTitleContainer}>
          <Text style={styles.appointmentTitle}>
            Appointment #{appointmentNumber}
          </Text>
          {optionLabel && (
            <Text style={styles.optionBadge}>Option {optionLabel}</Text>
          )}
        </View>
        <View style={styles.appointmentMeta}>
          <Text>
            <Text style={styles.metaLabel}>Fee: </Text>
            <Text style={styles.metaValue}>${fee.toLocaleString("en-US")}</Text>
          </Text>
          <Text>
            <Text style={styles.metaLabel}>Duration: </Text>
            <Text style={styles.metaValue}>{duration} min</Text>
          </Text>
        </View>
      </View>

      {appointment.notes && (
        <Text style={styles.appointmentDescription}>{appointment.notes}</Text>
      )}

      {/* Procedures */}
      {appointment.steps
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((step) => (
          <ProcedureCard key={step.id} step={step} />
        ))}

      {/* Time separator */}
      {!isLast && appointment.timeframeLabel && (
        <TimeSeparator timeframeLabel={appointment.timeframeLabel} />
      )}
    </View>
  );
};

// Main PDF Document component
interface TreatmentPlanPdfProps {
  treatmentPlan: TreatmentPlanResponse | PublicTreatmentPlanResponse;
  clinicName?: string;
  clinicEmail?: string;
  clinicPhone?: string;
  clinicWebsite?: string;
  signature?: string;
  signatureMethod?: "click" | "draw";
}

const TreatmentPlanPdf: React.FC<TreatmentPlanPdfProps> = ({
  treatmentPlan,
  clinicName = "Advanced Oral Specialty Group",
  clinicEmail = "Office@aosgdental.com",
  clinicPhone = "(856) 880-7558, (856) 830-6469",
  clinicWebsite = "advancedoralspecialtygroup.com",
  signature,
  signatureMethod,
}) => {
  // Build names
  const patientName =
    [treatmentPlan.patientFirstName, treatmentPlan.patientLastName]
      .filter(Boolean)
      .join(" ") || "Patient";
  const providerName =
    [treatmentPlan.providerFirstName, treatmentPlan.providerLastName]
      .filter(Boolean)
      .join(" ") || "Provider";

  // Sort appointments
  const sortedAppointments = [...treatmentPlan.appointments].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

  // Check if treatment plan has options (any appointment with conditionalPath)
  // If any appointment has conditionalPath, total fee should be 0
  const hasOptions = treatmentPlan.appointments.some(
    (appointment) =>
      appointment.conditionalPath && appointment.conditionalPath.trim() !== "",
  );

  // Calculate total fee - show $0 if multiple options
  const totalFee = hasOptions
    ? 0
    : "totalFee" in treatmentPlan && treatmentPlan.totalFee != null
      ? treatmentPlan.totalFee
      : "fee" in treatmentPlan
        ? parseFloat(treatmentPlan.fee || "0")
        : 0;

  // Check if approved by patient (status 5) and get approval date
  const isApprovedByPatient =
    "status" in treatmentPlan && treatmentPlan.status === 5;
  const approvedByPatientAt =
    "approvedByPatientAt" in treatmentPlan
      ? treatmentPlan.approvedByPatientAt
      : null;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Logo />
          <Text style={styles.title}>Treatment Plan For {patientName}</Text>
          <Text style={styles.subtitle}>
            {formatDate(treatmentPlan.dateGenerated)} |{" "}
            {formatTime(treatmentPlan.dateGenerated)}
            {isApprovedByPatient &&
              approvedByPatientAt &&
              ` | Approved by ${patientName} on ${formatDate(approvedByPatientAt)} ${formatTime(approvedByPatientAt)}`}
          </Text>
        </View>

        {/* Persons info */}
        <View style={styles.personsInfo}>
          <View>
            <Text style={styles.personLabel}>Patient</Text>
            <Text style={styles.personName}>{patientName}</Text>
          </View>
          <View>
            <Text style={styles.personLabel}>Initiated by</Text>
            <Text style={styles.personName}>{providerName}</Text>
          </View>
        </View>

        {/* Treatment Summary */}
        {treatmentPlan.treatmentSummary && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Treatment Summary</Text>
            <Text style={styles.sectionText}>
              {treatmentPlan.treatmentSummary}
            </Text>
          </View>
        )}

        {/* Provider Note */}
        {"providerNote" in treatmentPlan && treatmentPlan.providerNote && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Note from Provider</Text>
            <Text style={styles.sectionText}>{treatmentPlan.providerNote}</Text>
          </View>
        )}

        {/* Treatment Steps */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Treatment Steps</Text>
          {sortedAppointments.map((appointment, index) => (
            <Appointment
              key={appointment.id}
              appointment={appointment}
              appointmentNumber={index + 1}
              isLast={index === sortedAppointments.length - 1}
            />
          ))}
        </View>

        {/* Disclaimer */}
        <View style={styles.disclaimer}>
          <Text>
            The time shown between treatment steps is an estimate and may change
            as treatment progresses.
          </Text>
          <Text>Fees might vary based on your insurance coverage.</Text>
        </View>

        {/* Total Fee */}
        <View style={styles.totalFee}>
          <Text>
            <Text style={styles.totalFeeLabel}>Total Care Cost: </Text>
            <Text style={styles.totalFeeValue}>
              ${totalFee.toLocaleString("en-US")}
            </Text>
          </Text>
        </View>

        {/* Footer */}
        <View style={styles.footer} wrap={false}>
          <View style={styles.footerLeft}>
            <Text style={styles.footerGenerated}>
              File generated: {formatDate(treatmentPlan.dateGenerated)}
            </Text>
            <Text style={styles.footerClinicName}>{clinicName}</Text>
            <Text style={styles.footerClinicInfo}>
              {clinicEmail} | {clinicPhone} | {clinicWebsite}
            </Text>
          </View>

          {/* Signature section (if provided) */}
          {signature && (
            <View style={styles.signatureSection}>
              <Text style={styles.signatureLabel}>Patient Signature:</Text>
              {signatureMethod === "click" ? (
                <Text style={styles.signatureText}>{signature}</Text>
              ) : (
                <Image src={signature} style={styles.signatureImage} />
              )}
              <Text style={styles.signatureDate}>
                Signed: {formatDate(new Date().toISOString())}
              </Text>
            </View>
          )}
        </View>
      </Page>
    </Document>
  );
};

export default TreatmentPlanPdf;
